import React from "react";
import { FastField, Form, Formik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import GoogleLogin from "react-google-login";
import { FormGroup } from "reactstrap";
import { Box, Button, makeStyles } from "@material-ui/core";
import classNames from "classnames";

import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import SelectField from "components/Select/SelectField";
import { YupValidations } from "utils/validate";
import AppConfig from "config/AppConfig";
import Google from "components/Icon/Google";
import Facebook from "components/Icon/Facebook";
import AppColor from "utilities/AppColor";
import AppCheckbox from "components/Input/AppCheckbox";
import { useHistory } from "react-router-dom";
import AppleSignin from "react-apple-signin-auth";
import { Apple } from "@material-ui/icons";
import { WORKER_TYPE } from "config/AppConfig";

const { PATTERNS } = AppConfig;

const styles = makeStyles((theme) => ({
  margin: {
    marginTop: theme.spacing(3),
  },
  button: {
    fontSize: 15,
    fontWeight: "bold",
    borderRadius: 10,
    textTransform: "none",
    "& .MuiButton-label": {
      justifyContent: "space-between",
    },
  },
  signupWith: {
    display: "flex",
    justifyContent: "center",
    margin: "1.5rem 0",
    "&:after": {
      content: '""',
      flex: "1 1",
      borderBottom: "1px solid",
      margin: "auto",
      marginLeft: "0.5rem",
    },
    "&:before": {
      content: '""',
      flex: "1 1",
      borderBottom: "1px solid",
      margin: "auto",
      marginRight: "0.5rem",
    },
  },
  link: {
    textDecoration: "underline",
    color: AppColor.blue,
    cursor: "pointer",
  },
  disabled: {
    opacity: "0.6",
    pointerEvents: "none",
    backgroundColor: "#e9ecef",
    boxShadow: "0px 4px 4px #00000040",
  },
  title: {
    display: "flex",
    margin: "2rem 0 1rem 0",
    "& h5": {
      color: AppColor.mainColor,
    },
  },
}));

const SignUpForm = (props) => {
  const {
    onSubmit,
    onCancel,
    isSubmitting,
    clientType,
    workerType,
    isSignUpCompany,
    onFacebookResponse,
    onGoogleResponse,
    onAppleResponse,
  } = props;

  const { t } = useTranslation();
  const classes = styles();
  const history = useHistory();
  // const [latLng, setLatLng] = useState({});

  // const { lat, lng } = latLng || {};

  const initFormData = {
    role: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const roleOptions = Object.values(WORKER_TYPE).map(workerType => ({
    value: workerType,
    label: t(`worker_type.${workerType}`)
  }));

  const onGoogleFailed = (data) => {
    console.log(data);
  };

  const onFacebookFailed = (data) => {
    console.log(data);
  }

  const confirmTest = () => (
<p>
              <span>{t("text.signupMessage1")} </span>
              <span>{t("text.signupMessage2")}</span>
              <span
                className={classes.link}
                onClick={() => history.location.pathname.includes('company') ? history.push('/auth/company/login') : history.push("/term-of-use")}
              >
                {t("text.termOfUse")}
              </span>
              <span>{t("text.signupMessage3")}</span>
            </p>
  )
  
  return (
    <Formik
      enableReinitialize={true}
      onSubmit={onSubmit}
      autoComplete={false}
      initialValues={initFormData}
      validationSchema={Yup.object().shape({
        role: Yup.string().required(t("validation.required")),
        firstName: Yup.string()
          .required(t("validation.required"))
          .matches(PATTERNS.BLANK_SPACES, "validation.blankSpaces")
          .max(64, "validation.max_length_64"),
        lastName: Yup.string()
          .required(t("validation.required"))
          .matches(PATTERNS.BLANK_SPACES, "validation.blankSpaces")
          .max(64, "validation.max_length_64"),
        email: Yup.string().trim()
          .required(t("validation.required"))
          .matches(PATTERNS.BLANK_SPACES, "validation.blankSpaces")
          .max(64, "validation.max_length_64")
          .email(t("validation.email.invalid")),
        password: YupValidations.password,
        confirmPassword: YupValidations.confirmPassword,
      })}
    >
      {({ values }) => {
        return (
          <Form autoComplete="off" aria-autocomplete="none">
              <FastField
                component={SelectField}
                name="role"
                id="role"
                options={roleOptions}
                placeholder={t("label.select_a_role")}
                label={t("label.select_a_role")}
                required
              />
              <React.Fragment>
                <FastField
                  component={AppInput}
                  name="firstName"
                  id="firstName"
                  placeholder={t("label.firstName")}
                  label={t("label.firstName")}
                />
                <FastField
                  component={AppInput}
                  name="lastName"
                  id="lastName"
                  placeholder={t("label.lastName")}
                  label={t("label.lastName")}
                />
                <FastField
                  component={AppInput}
                  name="email"
                  id="email"
                  placeholder={t("label.email")}
                  label={t("label.email")}
                />
                <FastField
                  component={AppInput}
                  name="password"
                  id="password"
                  type="password"
                  placeholder={t("label.password")}
                  label={t("label.password")}
                />
                <FastField
                  component={AppInput}
                  name="confirmPassword"
                  id="confirmPassword"
                  type="password"
                  placeholder={t("label.confirmPassword")}
                  label={t("label.confirmPassword")}
                />
              </React.Fragment>

              <React.Fragment>
                <Box className={classes.signupWith}>{t("text.signupWith")}</Box>
                <Box mb={4}>
                  <FacebookLogin
                    appId={process.env.REACT_APP_FACEBOOK_CLIENT_ID}
                    fields="name,email,picture"
                    isSdkLoaded={true}
                    callback={(res) => onFacebookResponse(res, values.role)}
                    disableMobileRedirect
                    onFailure={onFacebookFailed}
                    render={({ onClick }) => (
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        size="large"
                        fullWidth
                        className={classNames({
                          [classes.button]: true,
                          [classes.disabled]: !values.confirmCollectInformation,
                        })}
                        style={{
                          backgroundColor: AppColor.blueDark,
                        }}
                        startIcon={<Facebook style={{ width: 35 }} />}
                        onClick={onClick}
                      >
                        <span style={{ width: "100%" }}>
                          {t("text.signupWithFacebook")}
                        </span>
                      </Button>
                    )}
                  />
                  <GoogleLogin
                    clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
                    onSuccess={(res) => onGoogleResponse(res, values.role)}
                    onFailure={onGoogleFailed}
                    isSignedIn={false}
                    autoLoad={false}
                    redirectUri={`${window.location.origin}`}
                    prompt="select_account"
                    cookiePolicy={"single_host_origin"}
                    render={({ onClick }) => (
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        size="large"
                        fullWidth
                        className={classNames({
                          [classes.margin]: true,
                          [classes.button]: true,
                          [classes.disabled]: !values.confirmCollectInformation,
                        })}
                        style={{
                          backgroundColor: AppColor.blue,
                        }}
                        onClick={onClick}
                        startIcon={<Google style={{ width: 35 }} />}
                      >
                        <span style={{ width: "100%" }}>
                          {t("text.signupWithGoogle")}
                        </span>
                      </Button>
                    )}
                  />
                  {!isSignUpCompany && (
                    <AppleSignin
                      /** Auth options passed to AppleID.auth.init() */
                      authOptions={{
                        /** Client ID - eg: 'com.example.com' */
                        clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
                        /** Requested scopes, seperated by spaces - eg: 'email name' */
                        scope: "email name",
                        /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
                        redirectURI:
                          `${window.location.origin}${window.location.pathname}` || process.env.REACT_APP_APPLE_SIGN_IN_REDIRECT_URL,
                        /** State string that is returned with the apple response */
                        state: "state",
                        /** Nonce */
                        nonce: "nonce",
                        /** Uses popup auth instead of redirection */
                        usePopup: true,
                      }} // REQUIRED
                      /** General props */
                      uiType="dark"
                      /** className */
                      className="apple-auth-btn"
                      /** Removes default style tag */
                      noDefaultStyle={false}
                      /** Allows to change the button's children, eg: for changing the button text */
                      buttonExtraChildren="Sign up with Apple"
                      /** Extra controlling props */
                      /** Called upon signin success in case authOptions.usePopup = true -- which means auth is handled client side */
                      onSuccess={(response) =>
                        onAppleResponse && onAppleResponse(response, values.role)
                      } // default = undefined
                      /** Called upon signin error */
                      onError={(error) => console.error(error)} // default = undefined
                      /** Skips loading the apple script if true */
                      skipScript={false} // default = undefined
                      /** Apple image props */
                      iconProp={{ style: { marginTop: "10px" } }} // default = undefined
                      /** render function - called with all props - can be used to fully customize the UI by rendering your own component  */
                      render={({ onClick }) => (
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          size="large"
                          fullWidth
                          className={classNames({
                            [classes.margin]: true,
                            [classes.button]: true,
                            [classes.disabled]: !values.confirmCollectInformation,
                          })}
                          style={{
                            backgroundColor: AppColor.black,
                          }}
                          onClick={onClick}
                          startIcon={
                            <Apple style={{ width: 35, height: 35 }} />
                          }
                        >
                          <span style={{ width: "100%" }}>
                            {t("button.signup_with_apple")}
                          </span>
                        </Button>
                      )}
                    />
                  )}
                </Box>
              </React.Fragment>
            <Box>
              <FastField
                component={AppCheckbox}
                label={confirmTest}
                id="confirmCollectInformation"
                name="confirmCollectInformation"
              />
            </Box>
            <FormGroup className={"d-flex justify-content-between mt-4"}>
              <AppButton
                label={`${t("button.back")}`}
                onClick={() => onCancel()}
                mode="outlined"
              />
              <AppButton
                label={`${t("title.finish")}`}
                rounder
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting || !values.confirmCollectInformation}
              />
            </FormGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

SignUpForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  isSubmitting: PropTypes.bool,
  isSignUpCompany: PropTypes.bool,
  onGoogleResponse: PropTypes.func,
  onFacebookResponse: PropTypes.func,
  onAppleResponse: PropTypes.func,
  clientType: PropTypes.any,
  workerType: PropTypes.any,
};

export default SignUpForm;
